import { useState, useEffect } from "react";
import {
  Shield, Zap, Smartphone, Gauge, Lock, Accessibility, BarChart3, Compass,
  CheckCircle2, AlertTriangle, XCircle, Lightbulb, FileText, Globe, RefreshCw
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authH() { return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }; }

const CATEGORY_META = {
  seo: { label: "Score SEO", icon: BarChart3, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  performance: { label: "Performance", icon: Zap, color: "text-amber-400", bg: "bg-amber-500/10" },
  mobile: { label: "Compatibilité mobile", icon: Smartphone, color: "text-blue-400", bg: "bg-blue-500/10" },
  security: { label: "Sécurité", icon: Lock, color: "text-purple-400", bg: "bg-purple-500/10" },
  accessibility: { label: "Accessibilité", icon: Accessibility, color: "text-cyan-400", bg: "bg-cyan-500/10" },
};

function ScoreRing({ score, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={8} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white font-['Outfit']">{score}</span>
        <span className="text-white/30 text-[10px] font-['Manrope']">/100</span>
      </div>
    </div>
  );
}

function ScoreCard({ category, score }) {
  const meta = CATEGORY_META[category] || { label: category, icon: BarChart3, color: "text-white", bg: "bg-white/10" };
  const Icon = meta.icon;
  const scoreColor = score >= 80 ? "text-emerald-400" : score >= 50 ? "text-amber-400" : "text-red-400";

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${meta.bg}`}>
        <Icon className={`w-5 h-5 ${meta.color}`} />
      </div>
      <div className="flex-1">
        <p className="text-white/60 text-xs font-['Manrope']">{meta.label}</p>
        <p className={`text-xl font-bold font-['Outfit'] ${scoreColor}`}>{score}/100</p>
      </div>
    </div>
  );
}

function CheckItem({ check }) {
  const statusConfig = {
    ok: { icon: CheckCircle2, color: "text-emerald-400" },
    warning: { icon: AlertTriangle, color: "text-amber-400" },
    error: { icon: XCircle, color: "text-red-400" },
  };
  const cfg = statusConfig[check.status] || statusConfig.warning;
  const Icon = cfg.icon;

  return (
    <div className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
      <Icon className={`w-4 h-4 shrink-0 ${cfg.color}`} />
      <span className="text-white/70 text-sm font-['Manrope'] flex-1">{check.name}</span>
      <span className="text-white/30 text-xs font-['Manrope']">{check.detail}</span>
    </div>
  );
}

export default function HealthTab() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/site-health`, { headers: authH() });
      setHealth(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchHealth(); }, []);

  if (loading) return <div className="text-center text-white/30 py-12 font-['Manrope']">Analyse du site en cours...</div>;
  if (!health) return <div className="text-center text-white/30 py-12 font-['Manrope']">Impossible de charger l'analyse.</div>;

  return (
    <div data-testid="health-tab" className="space-y-6">
      {/* Global score */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-8 flex flex-col items-center gap-4">
        <h3 className="text-white/60 text-xs uppercase tracking-wider font-['Manrope']">Score global du site</h3>
        <ScoreRing score={health.global_score} />
        <p className="text-white/40 text-sm font-['Manrope']">
          {health.global_score >= 80 ? "Excellent ! Votre site est bien optimisé." :
           health.global_score >= 60 ? "Bien, mais des améliorations sont possibles." :
           "Des optimisations sont nécessaires."}
        </p>
      </div>

      {/* Category scores */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {Object.entries(health.scores || {}).map(([cat, score]) => (
          <ScoreCard key={cat} category={cat} score={score} />
        ))}
      </div>

      {/* Site info cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <FileText className="w-4 h-4 text-white/30 mb-2" />
          <p className="text-white text-sm font-semibold font-['Manrope']">{health.pages?.indexed || 0}/{health.pages?.total || 0}</p>
          <p className="text-white/40 text-[11px] font-['Manrope']">Pages analysées</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <Globe className="w-4 h-4 text-white/30 mb-2" />
          <p className={`text-sm font-semibold font-['Manrope'] ${health.sitemap_detected ? "text-emerald-400" : "text-red-400"}`}>
            {health.sitemap_detected ? "Détecté" : "Non trouvé"}
          </p>
          <p className="text-white/40 text-[11px] font-['Manrope']">Sitemap XML</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <FileText className="w-4 h-4 text-white/30 mb-2" />
          <p className={`text-sm font-semibold font-['Manrope'] ${health.robots_detected ? "text-emerald-400" : "text-red-400"}`}>
            {health.robots_detected ? "Valide" : "Non trouvé"}
          </p>
          <p className="text-white/40 text-[11px] font-['Manrope']">Robots.txt</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <Lock className="w-4 h-4 text-white/30 mb-2" />
          <p className={`text-sm font-semibold font-['Manrope'] ${health.ssl_active ? "text-emerald-400" : "text-red-400"}`}>
            {health.ssl_active ? "Actif" : "Inactif"}
          </p>
          <p className="text-white/40 text-[11px] font-['Manrope']">Certificat SSL</p>
        </div>
      </div>

      {/* Checks */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5">
        <h4 className="text-white/60 text-xs uppercase tracking-wider font-['Manrope'] mb-3">Vérifications techniques</h4>
        <div className="divide-y divide-white/5">
          {health.checks?.map((c, i) => <CheckItem key={i} check={c} />)}
        </div>
      </div>

      {/* Recommendations */}
      {health.recommendations?.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-amber-400" />
            <h4 className="text-amber-400 text-xs uppercase tracking-wider font-['Manrope'] font-semibold">
              Recommandations d'amélioration
            </h4>
          </div>
          <ul className="space-y-2">
            {health.recommendations.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/60 font-['Manrope']">
                <span className="text-amber-400 mt-0.5">-</span>
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button data-testid="refresh-health" onClick={fetchHealth}
        className="flex items-center gap-2 mx-auto bg-white/5 border border-white/10 text-white/50 px-4 py-2 rounded text-xs hover:bg-white/10 transition font-['Manrope']">
        <RefreshCw className="w-3.5 h-3.5" /> Relancer l'analyse
      </button>
    </div>
  );
}
